/* Service Worker registration helper.
   Registers /sw.js in both dev and production (dev preview servers still
   support SW as long as the file is at web root).

   The `onUpdateAvailable` callback fires when a new Service Worker has
   installed but is still "waiting" to take control - the caller shows a
   banner to the user and calls `applyUpdate()` when they click Refresh.
*/
let waitingWorker = null;
let updateCallbacks = [];

export function onServiceWorkerUpdate(cb) {
    if (typeof cb === "function") {
        updateCallbacks.push(cb);
        // Fire immediately if an update is already waiting on registration
        if (waitingWorker) cb();
        return () => {
            updateCallbacks = updateCallbacks.filter((c) => c !== cb);
        };
    }
    return () => {};
}

/** Tell the waiting Service Worker to skipWaiting() and take control.
 *  When it activates, `controllerchange` fires and we reload the page. */
export function applyServiceWorkerUpdate() {
    if (waitingWorker) {
        waitingWorker.postMessage("SKIP_WAITING");
    }
}

export function registerServiceWorker() {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    // When the browser installs a new SW and it takes control, reload so the
    // page runs with the fresh cache/JS bundle.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
    });

    window.addEventListener("load", () => {
        navigator.serviceWorker
            .register("/sw.js", { scope: "/" })
            .then((reg) => {
                // Already-waiting SW from a previous visit
                if (reg.waiting && navigator.serviceWorker.controller) {
                    waitingWorker = reg.waiting;
                    updateCallbacks.forEach((cb) => cb());
                }
                reg.addEventListener("updatefound", () => {
                    const sw = reg.installing;
                    if (!sw) return;
                    sw.addEventListener("statechange", () => {
                        if (sw.state === "installed" && navigator.serviceWorker.controller) {
                            // New SW installed while an existing one is
                            // active → the user has an update available.
                            waitingWorker = sw;
                            updateCallbacks.forEach((cb) => cb());
                        }
                    });
                });
                // Periodically re-check for updates while the user has the tab open.
                setInterval(() => {
                    reg.update().catch(() => {});
                }, 60 * 60 * 1000); // hourly
            })
            .catch((err) => {
                // Non-fatal - the site still works without a SW, just no offline / push.
                console.warn("Service Worker registration failed:", err);
            });
    });
}

/* ------------------------ Web Push helpers ------------------------ */

/** Convert a URL-safe base64 VAPID public key into the Uint8Array PushManager expects. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Whether the browser supports the pieces we need for push. */
export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Current permission state ("default" | "granted" | "denied" | "unsupported"). */
export function pushPermission() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

/** Return the active PushSubscription (or null). */
export async function getPushSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Ask the user for notification permission, subscribe via PushManager,
 * and register the subscription with the backend. Returns the subscription
 * on success, or null on failure / denial.
 */
export async function subscribeToPush(apiClient) {
  if (!pushSupported()) throw new Error('Twoja przeglądarka nie obsługuje powiadomień push.');

  // Ask permission first - required by all browsers before .subscribe().
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error(perm === 'denied' ? 'Odmówiono uprawnień do powiadomień.' : 'Powiadomienia nie zostały włączone.');
  }

  const reg = await navigator.serviceWorker.ready;

  // Fetch VAPID public key from backend. We add a cache-busting query param
  // and no-cache headers because some clients (Cloudflare edge, older
  // browsers, PWA runtime caches) can hold onto a stale response from before
  // the server was configured - in which case we'd wrongly show
  // "Serwer nie skonfigurował powiadomień." even though the key is set now.
  const { data } = await apiClient.get('/push/vapid-public-key', {
    params: { _: Date.now() },
    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
  });
  if (!data?.key) throw new Error('Serwer nie skonfigurował powiadomień.');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
  }

  const payload = JSON.parse(JSON.stringify(sub));
  await apiClient.post('/push/subscribe', {
    endpoint: payload.endpoint,
    keys: payload.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

/** Unsubscribe both locally and on the server. */
export async function unsubscribeFromPush(apiClient) {
  const sub = await getPushSubscription();
  if (!sub) return true;
  try {
    await apiClient.post('/push/unsubscribe', {
      endpoint: sub.endpoint,
      keys: sub.toJSON().keys || { p256dh: '', auth: '' },
    });
  } catch {
    /* server may be down; still unsubscribe locally */
  }
  await sub.unsubscribe();
  return true;
}
